import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useClient } from '@/hooks/use-client';
import { useScopeUrl } from '@/hooks/use-scope-url';

import type { AsyncRefresh } from 'pl-api';

type Reconciliation = {
  handle: string;
  state: 'pending' | 'success' | 'error';
  request: Promise<void>;
};

type RefreshSession = {
  id: number;
  version: number;
  reconciliation?: Reconciliation;
  retry?: () => void;
  listeners: Map<symbol, () => void>;
  reconciledHandles: Set<string>;
  requests: Map<string, Promise<AsyncRefresh>>;
};

const notifySession = (session: RefreshSession) => {
  session.version++;
  for (const notify of session.listeners.values()) notify();
};

/** Completion belongs to the thread, rather than the view that polled it. */
const reconcileSession = (
  session: RefreshSession,
  handle: string,
  refresh: () => Promise<void>,
) => {
  if (session.reconciliation?.state === 'pending') return session.reconciliation.request;
  if (session.reconciledHandles.has(handle)) return Promise.resolve();

  const entry: Reconciliation = { handle, state: 'pending', request: Promise.resolve() };
  session.reconciliation = entry;
  entry.request = Promise.resolve()
    .then(() => {
      if (session.listeners.size) return refresh();
    })
    .then(
      () => {
        if (!session.listeners.size) return;
        session.reconciledHandles.add(handle);
        session.reconciliation = { ...entry, state: 'success' };
        notifySession(session);
      },
      () => {
        if (!session.listeners.size) return;
        session.reconciliation = { ...entry, state: 'error' };
        notifySession(session);
      },
    );
  notifySession(session);
  return entry.request;
};

// A page and a media modal can display the same thread simultaneously. Only the
// first subscriber runs timers; the next takes over when that view closes.
const sessions = new WeakMap<ReturnType<typeof useClient>, Map<string, RefreshSession>>();
let nextId = 0;

const useRefreshSession = (statusId: string) => {
  const client = useClient();
  const scope = useScopeUrl();
  const key = JSON.stringify([scope, statusId]);
  const session = useMemo(() => {
    let threads = sessions.get(client);
    if (!threads) sessions.set(client, (threads = new Map()));
    let session = threads.get(key);
    if (!session) {
      session = {
        id: nextId++,
        version: 0,
        listeners: new Map(),
        reconciledHandles: new Set(),
        requests: new Map(),
      };
      threads.set(key, session);
    }
    return session;
  }, [client, key]);
  const token = useMemo(() => Symbol('thread-refresh-view'), [session]);
  const subscribe = useCallback(
    (listener: () => void) => {
      session.listeners.set(token, listener);
      notifySession(session);
      return () => {
        session.listeners.delete(token);
        notifySession(session);
        // Preserve the session through StrictMode's unsubscribe/resubscribe cycle.
        queueMicrotask(() => {
          if (!session.listeners.size && sessions.get(client)?.get(key) === session) {
            sessions.get(client)!.delete(key);
          }
        });
      };
    },
    [client, key, session, token],
  );
  const getSnapshot = useCallback(() => session.version, [session]);
  useSyncExternalStore(subscribe, getSnapshot);
  const isOwner = session.listeners.keys().next().value === token;

  return { session, isOwner };
};

export { useRefreshSession, reconcileSession, type RefreshSession };
